import { useEffect, useState } from 'react';

import { RhinoError } from '../../error';
import { queryClient } from '../queryClient';
import { queryKeys } from '../queryKeys';

import { getChannelProgramsFromCache } from './getChannelProgramsFromCache';
import { createChannelProgramsQueryOptions } from './queryOptions';

type UseFetchChannelProgramsParams = {
  days: Array<string>;
  channelId: number;
  withPlaceholders?: { startDate: number; endDate: number };
};

export function useFetchChannelPrograms(params: UseFetchChannelProgramsParams) {
  const { days, channelId, withPlaceholders } = params;

  const [data, setData] = useState(() => getChannelProgramsFromCache(channelId, withPlaceholders));
  const [error, setError] = useState<RhinoError>();

  const fetchPrograms = () => {
    if (!days || !channelId) {
      return;
    }

    days.forEach(async (day) => {
      try {
        await queryClient.fetchQuery(createChannelProgramsQueryOptions(channelId, day));
      } catch (error) {
        setError(error as RhinoError);
      }
    });
  };

  useEffect(() => {
    if (channelId !== undefined) {
      return queryClient.getQueryCache().subscribe((data) => {
        if (
          data.type === 'updated' &&
          data.action.type === 'success' &&
          Array.isArray(data.query.queryKey) &&
          data.query.queryKey[0] === queryKeys.channelProgramsAllDays(channelId)[0] &&
          data.query.queryKey[1] === channelId
        ) {
          setData(getChannelProgramsFromCache(channelId, withPlaceholders));
        }
      });
    }
  }, [channelId]);

  // TODO Investigate whether we can migrate to `useQueries`(+ memoized selector?) without performance degradation
  // We don't use `useQueries` bc programs arranging and adding placeholders is pretty heavy and
  // should be done only when data is changed, not on every hook call.
  useEffect(() => {
    fetchPrograms();
  }, [channelId, days.join()]);

  return { data, error };
}
